import { Add, Delete, Edit, Quiz, UploadFile } from "@mui/icons-material";
import BlindsClosedIcon from "@mui/icons-material/BlindsClosed";
import {
  Grid,
  IconButton,
  Skeleton,
  Stack,
  TableCell,
  Tooltip,
  Typography,
} from "@mui/material";
import EmptyContent from "components/EmptyContent";
import List from "components/UI/List";
import { useAlert } from "contexts/alertContext";
import { Feature } from "models/api";
import {
  IWorkshopEvaluationQuestion,
  IWorkshopEvaluationQuestionInput,
} from "models/workshopEvaluationQuestions";
import { IImportQuestionsInput } from "models/vendorAssessmentQuestion";
import React, { useCallback, useEffect, useState } from "react";
import { QueryClient, useMutation } from "react-query";

import {
  WorkshopEvaluationQuestionCells,
  addWorkshopEvaluationQuestion,
  deleteWorkshopEvaluationQuestion,
  editWorkshopEvaluationQuestion,
  getWorkshopEvaluationQuestions,
  importExcel,
} from "framework/workshopEvaluationQuestion";
import AddWorkshopEvaluationQuestion from "./partials/AddWorkshopEvaluationQuestion/AddWorkshopEvaluationQuestion";
import BlockIcon from "@mui/icons-material/Block";
import EditWorkshopEvaluationQuestion from "./partials/EditWorkshopEvaluationQuestion/EditWorkshopEvaluationQuestion";
import DeleteWorkshopEvaluationQuestion from "./partials/DeleteWorkshopEvaluationQuestion/DeleteWorkshopEvaluationQuestion";
import ImportQuestions from "./partials/ImportQuestions/ImportQuestions";
import { getWorkshopEvaluationQuestionDimensions } from "framework/workshopEvaluationQuestionDimension";
import { handleError } from "helpers/utils";
import { useTranslation } from "react-i18next";
import { IPermission } from "models/permissions";
import { getPermission } from "helpers";
import { Link, useParams } from "react-router-dom";
import { IEvaluationQuestionType } from "models/app";
import { IWorkshopEvaluationQuestionDimension } from "models/workshopEvaluationQuestionDimension";

interface Props {
  feature: Feature;
}

const WorkshopEvaluationQuestion: React.FC<Props> = ({ feature }) => {
  let typesOptions: IEvaluationQuestionType[] = [
    {
      id: 1,
      description: "Choice",
      descriptionAr: "",
    },
    {
      id: 2,
      description: "Open text",
      descriptionAr: "",
    },
  ];
  const urlParams = useParams();
  let id: string = urlParams[`${feature.singleName}Id`] as string;
  const [loading, setLoading] = useState<boolean>(true);
  const [loading3, setLoading3] = useState<boolean>(true);
  const { t, i18n } = useTranslation();
  const [permission, setPermission] = useState<IPermission | null>(null);
  const [loading2, setLoading2] = useState<boolean>(false);
  const [WorkshopEvaluationQuestion, setWorkshopEvaluationQuestion] = useState<
    IWorkshopEvaluationQuestion[]
  >([] as IWorkshopEvaluationQuestion[]);
  const [
    FilterWorkshopEvaluationQuestion,
    setFilterWorkshopEvaluationQuestion,
  ] = useState<IWorkshopEvaluationQuestion[]>(
    [] as IWorkshopEvaluationQuestion[]
  );
  const [
    workshopEvaluationQuestionDimensions,
    setWorkshopEvaluationQuestionDimensions,
  ] = useState<IWorkshopEvaluationQuestionDimension[]>(
    [] as IWorkshopEvaluationQuestionDimension[]
  );
  const [openAdd, setOpenAdd] = useState<boolean>(false);
  const [openEdit, setOpenEdit] = useState<boolean>(false);
  const [openDelete, setOpenDelete] = useState<boolean>(false);
  const [openImportQuestions, setOpenImport] = useState<boolean>(false);
  const [
    selectedWorkshopEvaluationQuestion,
    setSelectedWorkshopEvaluationQuestion,
  ] = useState<IWorkshopEvaluationQuestion>({} as IWorkshopEvaluationQuestion);
  const [inputObj, setInputObj] = useState<IWorkshopEvaluationQuestionInput>(
    {} as IWorkshopEvaluationQuestionInput
  );
  const [importQuestionsInputObj, setImportQuestionsInputObj] = useState<IImportQuestionsInput>(
    {} as IImportQuestionsInput
  );
  const { setAlert } = useAlert();

  // eslint-disable-next-line
  const queryClient = new QueryClient();

  const addWorkshopEvaluationQuestionMutation = useMutation({
    mutationFn: (createInput: IWorkshopEvaluationQuestionInput) => {
      return addWorkshopEvaluationQuestion(createInput);
    },
  });

  const editWorkshopEvaluationQuestionMutation = useMutation({
    mutationFn: (data: {
      createInput: IWorkshopEvaluationQuestionInput;
      id: string;
    }) => {
      return editWorkshopEvaluationQuestion(data.createInput, data.id);
    },
  });

  const deleteWorkshopEvaluationQuestionMutation = useMutation({
    mutationFn: (data: { id: string }) => {
      return deleteWorkshopEvaluationQuestion(data.id);
    },
  });

  const importExcelMutation = useMutation({
    mutationFn: (createInput: FormData) => {
      return importExcel(createInput, id);
    },
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let response: any = await queryClient.fetchQuery(
        "get workshop evaluation questions",
        () =>
          getWorkshopEvaluationQuestions(
            `/byevaluation?workshopEvaluationId=${id}`
          )
      );
      response =
        response && response.length > 0
          ? response.map((item: IWorkshopEvaluationQuestion) => ({
            ...item,
          }))
          : [];
      setWorkshopEvaluationQuestion(response);
      setFilterWorkshopEvaluationQuestion(response);
      setLoading(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading(false);
    }

    // eslint-disable-next-line
  }, [queryClient, setAlert]);

  const fetchDimensions = useCallback(async () => {
    setLoading(true);
    try {
      let response: any = await queryClient.fetchQuery("get dimensions", () =>
        getWorkshopEvaluationQuestionDimensions()
      );
      response =
        response && response.length > 0
          ? response.map((item: IWorkshopEvaluationQuestion) => ({
            ...item,
          }))
          : [];
      setWorkshopEvaluationQuestionDimensions(response);
      setLoading(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading(false);
    }

    // eslint-disable-next-line
  }, [queryClient, setAlert]);

  const fetchPermission = useCallback(async () => {
    setLoading3(true);
    try {
      const res = await getPermission(2);
      setPermission(res);
      setLoading3(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading3(false);
    }

    // eslint-disable-next-line
  }, [queryClient, setAlert]);

  useEffect(() => {
    fetchData();
    fetchDimensions();
    fetchPermission();
    // eslint-disable-next-line
  }, []);

  if (loading || loading3)
    return (
      <Stack spacing={2} p={2}>
        {Array.from({ length: 10 }).map((_, i) => (
          <Skeleton variant="rectangular" key={i} width="100%" height={30} />
        ))}
      </Stack>
    );

  const handleChange = (name: string, value: any) => {
    if (name == "questionOrder" || name == "type") {
      value = parseInt(value);
    }
    setInputObj((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleOpenAdd = () => setOpenAdd(true);
  const handleCloseAdd = () => setOpenAdd(false);

  const handleOpenEdit = (
    WorkshopEvaluationQuestion: IWorkshopEvaluationQuestion
  ) => {
    setSelectedWorkshopEvaluationQuestion(WorkshopEvaluationQuestion);
    setOpenEdit(true);
  };
  const handleCloseEdit = () => {
    setOpenEdit(false);
    setSelectedWorkshopEvaluationQuestion({} as IWorkshopEvaluationQuestion);
  };

  const handleOpenDelete = (
    WorkshopEvaluationQuestion: IWorkshopEvaluationQuestion
  ) => {
    setSelectedWorkshopEvaluationQuestion(WorkshopEvaluationQuestion);
    setOpenDelete(true);
  };

  const handleCloseDelete = () => {
    setOpenDelete(false);
    setSelectedWorkshopEvaluationQuestion({} as IWorkshopEvaluationQuestion);
  };

  const handleOpenImport = () => {
    setOpenImport(true);
  };

  const handleCloseImport = () => {
    setOpenImport(false);
  };

  const getBodyCells = (row: IWorkshopEvaluationQuestion) => {
    let cells = Object.keys(row).map((key: string, i: number) => {
      if (key === "title")
        return {
          id: "title",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row.title}</Typography>
            </TableCell>
          ),
        };

      if (key === "titleAr")
        return {
          id: "titleAr",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row.titleAr}</Typography>
            </TableCell>
          ),
        };

      if (key === "dimension")
        return {
          id: "dimension",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row.dimension}</Typography>
            </TableCell>
          ),
        };

      if (key === "description")
        return {
          id: "description",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row.description}</Typography>
            </TableCell>
          ),
        };

      if (key === "descriptionAr")
        return {
          id: "descriptionAr",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row.descriptionAr}</Typography>
            </TableCell>
          ),
        };

      if (key === "questionOrder")
        return {
          id: "questionOrder",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row.questionOrder}</Typography>
            </TableCell>
          ),
        };

      if (key === "type")
        return {
          id: "type",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">
                {row.type == 1 ? "MCQ" : "Open Text"}
              </Typography>
            </TableCell>
          ),
        };

      if (key === "id")
        return {
          id: "actions",
          element: (
            <TableCell align={"right"} key={i}>
              <Stack
                direction={"row"}
                alignContent={"center"}
                justifyContent={i18n.language === "en" ? "start" : "end"}
                spacing={1}
              >
                {permission?.hasModifyPermission && (
                  <Tooltip title={t("T_edit")}>
                    <IconButton
                      size={"small"}
                      color={"primary"}
                      onClick={() => handleOpenEdit(row)}
                    >
                      <Edit />
                    </IconButton>
                  </Tooltip>
                )}

                {permission?.hasDeletePermission && (
                  <Tooltip title={t("T_delete")}>
                    <IconButton
                      size={"small"}
                      color={"error"}
                      onClick={() => handleOpenDelete(row)}
                    >
                      <Delete />
                    </IconButton>
                  </Tooltip>
                )}

                <Tooltip title={t("T_questions")}>
                  <Link to={`/workshopEvaluationQuestionOptions/${row.id}`}>
                    <IconButton size={"small"} color={"info"}>
                      <Quiz />
                    </IconButton>
                  </Link>
                </Tooltip>
              </Stack>
            </TableCell>
          ),
        };
    });
    return cells;
  };

  const handleAdd = async () => {
    if (
      !inputObj.title ||
      !inputObj.titleAr ||
      !inputObj.workshopEvaluationQuestionDimensionId ||
      !inputObj.description ||
      !inputObj.descriptionAr ||
      !inputObj.questionOrder ||
      !inputObj.type
    )
      return setAlert({
        open: true,
        message: "Please fill all fields",
        type: "error",
      });
    try {
      setLoading2(true);
      inputObj.workshopEvaluationId = id;
      await addWorkshopEvaluationQuestionMutation.mutateAsync(inputObj);
      setAlert({
        open: true,
        message: "WorkshopEvaluationQuestion added successfully",
        type: "success",
      });
      fetchData();
      handleCloseAdd();
      setInputObj({} as IWorkshopEvaluationQuestionInput);
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleEdit = async () => {
    try {
      setLoading2(true);
      let createInput: IWorkshopEvaluationQuestionInput = {
        workshopEvaluationId: id,
        title: inputObj.title || selectedWorkshopEvaluationQuestion.title,
        titleAr: inputObj.titleAr || selectedWorkshopEvaluationQuestion.titleAr,
        description:
          inputObj.description ||
          selectedWorkshopEvaluationQuestion.description,
        descriptionAr:
          inputObj.descriptionAr ||
          selectedWorkshopEvaluationQuestion.descriptionAr,
        questionOrder:
          inputObj.questionOrder ||
          selectedWorkshopEvaluationQuestion.questionOrder,
        type: inputObj.type || selectedWorkshopEvaluationQuestion.type,
        workshopEvaluationQuestionDimensionId:
          inputObj.workshopEvaluationQuestionDimensionId ||
          selectedWorkshopEvaluationQuestion.workshopEvaluationQuestionDimensionId,
      };
      await editWorkshopEvaluationQuestionMutation.mutateAsync({
        createInput,
        id: selectedWorkshopEvaluationQuestion.id,
      });
      setAlert({
        open: true,
        message: "WorkshopEvaluationQuestion updated successfully",
        type: "success",
      });
      fetchData();
      handleCloseEdit();
      setInputObj({} as IWorkshopEvaluationQuestionInput);
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading2(true);
      await deleteWorkshopEvaluationQuestionMutation.mutateAsync({
        id: selectedWorkshopEvaluationQuestion.id,
      });
      setAlert({
        open: true,
        message: "WorkshopEvaluationQuestion deleted successfully",
        type: "success",
      });
      fetchData();
      handleCloseDelete();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  function handleImportQuestionsChange(name: string, value: any): void {
    setImportQuestionsInputObj((prev) => ({
      ...prev,
      [name]: value,
    }));
  }

  async function handleImportQuestions(): Promise<void> {
    try {
      setLoading2(true);
      const createInput = new FormData();
      if (importQuestionsInputObj.excelFile) createInput.append("ExcelFile", importQuestionsInputObj.excelFile[0] || importQuestionsInputObj.excelFile);
      await importExcelMutation.mutateAsync(createInput);
      setAlert({
        open: true,
        message: "Questions imported successfully",
        type: "success",
      });
      fetchData();
      handleCloseImport();
      setImportQuestionsInputObj({} as IImportQuestionsInput);
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  }

  return (
    <>
      <Stack spacing={2} p={2}>
        <Grid
          container
          justifyContent={"space-between"}
          alignItems="center"
          sx={{ mb: 4 }}
        >
          <Typography variant="h3" color={"header.primary"}>
            {t(feature.name)}
          </Typography>
          <Stack direction={"row"} spacing={1}>
            {permission?.hasModifyPermission && (
              <Tooltip title={t("T_add")}>
                <IconButton
                  sx={{
                    // bgcolor: 'primary.main',
                    color: "primary.main",
                    "&:hover": {
                      bgcolor: "primary.light",
                      color: "white",
                    },
                    height: 40,
                    width: 40,
                    transition: "0.5s",
                  }}
                  onClick={handleOpenAdd}
                >
                  <Add />
                </IconButton>
              </Tooltip>
            )}
            {permission?.hasModifyPermission && (
              <Tooltip title={t("T_import_questions")}>
                <IconButton
                  sx={{
                    // bgcolor: 'primary.main',
                    color: "primary.main",
                    "&:hover": {
                      bgcolor: "primary.light",
                      color: "white",
                    },
                    height: 40,
                    width: 40,
                    transition: "0.5s",
                  }}
                  onClick={handleOpenImport}
                >
                  <UploadFile />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Grid>

        {permission?.hasReadPermission! &&
          FilterWorkshopEvaluationQuestion.length > 0 && (
            <List
              headCells={WorkshopEvaluationQuestionCells}
              data={FilterWorkshopEvaluationQuestion}
              getBodyCells={getBodyCells}
              feature={feature}
              canEdit={false}
            />
          )}
        {(!FilterWorkshopEvaluationQuestion ||
          FilterWorkshopEvaluationQuestion.length === 0) &&
          permission?.hasReadPermission! && (
            <EmptyContent
              icon={<BlindsClosedIcon sx={{ fontSize: "3.5rem " }} />}
              header={t("T_no_question_header")}
              message={t("T_no_question_message")}
            />
          )}
        {!permission?.hasReadPermission && (
          <EmptyContent
            icon={<BlockIcon sx={{ fontSize: "3.5rem " }} />}
            header={t("T_no_permissions")}
            message={t("T_no_permissions_message")}
          />
        )}
      </Stack>

      {openAdd && (
        <AddWorkshopEvaluationQuestion
          open={openAdd}
          loading={loading2}
          dimensions={workshopEvaluationQuestionDimensions}
          types={typesOptions}
          onClose={handleCloseAdd}
          onSubmit={handleAdd}
          onChanges={handleChange}
        />
      )}
      {openEdit && (
        <EditWorkshopEvaluationQuestion
          open={openEdit}
          loading={loading2}
          dimensions={workshopEvaluationQuestionDimensions}
          types={typesOptions}
          workshopEvaluationQuestion={selectedWorkshopEvaluationQuestion}
          onClose={handleCloseEdit}
          onSubmit={handleEdit}
          onChanges={handleChange}
        />
      )}
      {openDelete && (
        <DeleteWorkshopEvaluationQuestion
          open={openDelete}
          loading={loading2}
          onClose={handleCloseDelete}
          onSubmit={handleDelete}
        />
      )}

      {openImportQuestions && (
        <ImportQuestions
          open={openImportQuestions}
          loading={loading2}
          onClose={handleCloseImport}
          onSubmit={handleImportQuestions}
          onChanges={handleImportQuestionsChange} />
      )}

    </>
  );
};

export default WorkshopEvaluationQuestion;
